import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';

import API from '@api';
import { withSsrCache } from '../../ssrCache';

/**
 * Товарные карусели главной (Рекомендуем / Новинки / Популярное).
 * xStoreId входит в ключ: смена магазина — новый кэш и новый запрос
 * (раньше это делала mobx-reaction в ProductsRow).
 */
const INDEX_PRODUCTS_STALE_TIME = 5 * 60 * 1000;
const INDEX_PRODUCTS_GC_TIME = 10 * 60 * 1000;

type IndexProductsRowParamsT = {
  /** Имя метода API: 'getIndexCore' | 'getIndexAddition' */
  resolver: string;
  /** Слой данных: 'popularProducts' | 'newbeeProducts' | 'recommendProducts' и т.п. */
  layer: string;
  xStoreId?: number | string | null;
};

export const indexProductsRowQueryOptions = (
  params: IndexProductsRowParamsT
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['indexProductsRow', params],
      queryFn: () => {
        const { resolver, layer } = params;

        let variables = {};
        if (layer === 'newbeeProducts' || layer === 'popularProducts') {
          variables = { blocksFilter: [layer] };
        }

        // Сигнатура вызова сохранена из старого ProductsRow
        return Promise.resolve(
          API[resolver]({ [layer]: true, blocksFilter: [layer] }, variables)
        );
      },
      staleTime: INDEX_PRODUCTS_STALE_TIME,
      gcTime: INDEX_PRODUCTS_GC_TIME,
      // xStoreId проставляется асинхронно (~1с после старта): ключ меняется
      // {xStoreId: null} -> {xStoreId: N}. Без keepPreviousData карусель в
      // этот момент схлопывается в пустую и «мигает» — держим предыдущие
      // данные до прихода новых, как старый useState в ProductsRow
      placeholderData: keepPreviousData
    }),
    'product'
  );

/** Блок «Приготовьте сегодня» на главной */
export const indexCoreRecipesQueryOptions = () =>
  withSsrCache(
    queryOptions({
      queryKey: ['indexCoreRecipes'],
      queryFn: () => API.getIndexCoreRecipes({}),
      staleTime: INDEX_PRODUCTS_STALE_TIME,
      gcTime: INDEX_PRODUCTS_GC_TIME
    })
  );

export const useIndexCoreRecipesQuery = () =>
  useQuery(indexCoreRecipesQueryOptions());

/**
 * Блок «Товар дня» на главной. xStoreId в ключе — смена магазина
 * означает новый запрос (раньше это делала mobx-reaction в компоненте).
 */
export const pofdProductsQueryOptions = (
  xStoreId?: number | string | null
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['pofdProducts', xStoreId ?? null],
      queryFn: () =>
        API.getIndexCore({ pofdProducts: true, imageSizeFilter: 'w240h240' }),
      staleTime: INDEX_PRODUCTS_STALE_TIME,
      gcTime: INDEX_PRODUCTS_GC_TIME,
      // xStoreId приходит асинхронно — держим предыдущие данные при смене ключа
      placeholderData: keepPreviousData
    }),
    'product',
    [xStoreId]
  );

export const usePofdProductsQuery = (xStoreId?: number | string | null) =>
  useQuery(pofdProductsQueryOptions(xStoreId));

/** Рекомендуемые товары из getIndexAddition (лендинг OysterBar, type='main-hits') */
export const indexAdditionRecommendProductsQueryOptions = (productsLimit = 6) =>
  withSsrCache(
    queryOptions({
      queryKey: ['indexAdditionRecommendProducts', productsLimit],
      queryFn: () =>
        API.getIndexAddition({ productsLimit, recommendProducts: true }),
      select: res => res?.getIndexAddition?.recommendProducts ?? [],
      staleTime: INDEX_PRODUCTS_STALE_TIME,
      gcTime: INDEX_PRODUCTS_GC_TIME
    }),
    'product'
  );

/** «Популярное» в блоке услуг каталога. xStoreId в ключе (раньше — mobx-reaction) */
export const popularMainPageItemsQueryOptions = (
  xStoreId?: number | string | null
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['popularMainPageItems', xStoreId ?? null],
      queryFn: () => API.getPopularMainPageItems(),
      select: res => res.getPopularMainPageItems ?? [],
      staleTime: INDEX_PRODUCTS_STALE_TIME,
      gcTime: INDEX_PRODUCTS_GC_TIME,
      placeholderData: keepPreviousData
    }),
    'product',
    [xStoreId]
  );

/** Популярные товары (нулевая выдача поиска). xStoreId в ключе — цены зависят от магазина */
export const indexCorePopularProductsQueryOptions = (
  xStoreId?: number | string | null
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['indexCorePopularProducts', xStoreId ?? null],
      queryFn: () =>
        API.getIndexCore({
          popularProducts: true,
          imageSizeFilter: 'w240h240'
        }),
      select: res => res.getIndexCore.popularProducts ?? [],
      staleTime: INDEX_PRODUCTS_STALE_TIME,
      gcTime: INDEX_PRODUCTS_GC_TIME,
      placeholderData: keepPreviousData
    }),
    'product',
    [xStoreId]
  );
